import { formatDateCompact } from '@/shared/utilities';
import type { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface User {
  fullName?: string;
}

interface UseMessageHeaderOptions {
  sentAt?: Dayjs | Date | string | number;
  hideName?: boolean;
  isOwn: boolean;
  user?: User | null;
}

interface HeaderContent {
  name: string;
  timeStr: string;
  ariaLabel: string;
}

/**
 * Custom hook for managing message header display logic
 * Extracts name, timestamp, and ARIA label computation from MessageLayout
 * @param options - Configuration for header rendering
 * @returns Header content with formatted name, time, and ARIA label, or null if hidden
 * @example
 * const headerContent = useMessageHeader({
 *   sentAt: message.sentAt,
 *   hideName: false,
 *   isOwn: true,
 *   user: currentUser
 * });
 */
export function useMessageHeader({ sentAt, hideName, isOwn, user }: UseMessageHeaderOptions): HeaderContent | null {
  const { t } = useTranslation();

  return useMemo(() => {
    if (!sentAt || hideName) return null;

    const name = isOwn ? t('messages.layout.you') : user?.fullName || t('messages.layout.unknown');
    const timeStr = formatDateCompact(sentAt);

    // biome-ignore lint/style/useTemplate: <explanation>
    const ariaLabel = t('messages.aria.messageFrom', { name }) + ' ' + t('messages.aria.messageAt', { time: timeStr });

    return { name, timeStr, ariaLabel };
  }, [sentAt, hideName, isOwn, user?.fullName, t]);
}
